import React, { useState } from 'react';
import { ArrowRight, Shield, Target, Search, FileText, CheckCircle, Clock, Users, Award } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const PenetrationTestingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const testingTypes = [
    {
      icon: Target,
      title: "External Penetration Testing",
      description: "Simulate real-world attacks from outside your network perimeter to identify vulnerabilities in your external-facing systems.",
      features: ["Network reconnaissance", "Vulnerability exploitation", "Social engineering tests", "Physical security assessment"]
    },
    {
      icon: Shield,
      title: "Internal Penetration Testing",
      description: "Test your internal network security by simulating attacks from within your organization's network infrastructure.",
      features: ["Lateral movement testing", "Privilege escalation", "Internal network mapping", "Domain controller attacks"]
    },
    {
      icon: Search,
      title: "Web Application Testing",
      description: "Comprehensive security testing of your web applications to identify OWASP Top 10 vulnerabilities and custom attack vectors.",
      features: ["SQL injection testing", "XSS vulnerability assessment", "Authentication bypass", "Business logic flaws"]
    },
    {
      icon: FileText,
      title: "Mobile Application Testing",
      description: "Security assessment of iOS and Android applications including static and dynamic analysis of mobile app security.",
      features: ["Static code analysis", "Dynamic runtime testing", "API security testing", "Data storage security"]
    }
  ];

  const methodology = [
    {
      step: "01",
      title: "Planning & Reconnaissance",
      description: "Define scope, gather intelligence, and identify potential attack vectors through passive and active reconnaissance.",
      duration: "1-2 days"
    },
    {
      step: "02", 
      title: "Scanning & Enumeration",
      description: "Perform network scanning, service enumeration, and vulnerability identification using automated and manual techniques.",
      duration: "2-3 days"
    },
    {
      step: "03",
      title: "Gaining Access",
      description: "Attempt to exploit identified vulnerabilities to gain unauthorized access to systems and applications.",
      duration: "3-5 days"
    },
    {
      step: "04",
      title: "Maintaining Access",
      description: "Test persistence mechanisms and evaluate the ability to maintain long-term access to compromised systems.",
      duration: "1-2 days"
    },
    {
      step: "05",
      title: "Analysis & Reporting",
      description: "Document findings, assess business impact, and provide detailed remediation recommendations.",
      duration: "2-3 days"
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-slate-100' 
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Penetration Testing"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-blue-950/90' 
              : 'bg-gradient-to-r from-blue-50/90 to-purple-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Target className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Penetration <span className={isDarkMode ? "text-blue-400" : "text-blue-600"}>Testing</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Identify vulnerabilities before attackers do with comprehensive penetration testing 
              that simulates real-world cyber attacks on your systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-blue-500/25" 
                    : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-blue-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-slate-900" 
                    : "border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                }`}
              >
                Request Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testing Types Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Comprehensive Testing Services
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Our penetration testing services cover all aspects of your security infrastructure
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testingTypes.map((type, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <type.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{type.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {type.description}
                </p>
                <ul className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Our Testing Methodology
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              A systematic approach following industry best practices and standards
            </p>
          </div>
          
          <div className="space-y-8">
            {methodology.map((phase, index) => (
              <div key={index} className={`flex items-start space-x-6 p-6 rounded-xl ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              } shadow-lg`}>
                <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                  isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
                }`}>
                  {phase.step}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-xl font-semibold ${
                      isDarkMode ? 'text-slate-100' : 'text-slate-900'
                    }`}>{phase.title}</h3>
                    <div className="flex items-center space-x-2">
                      <Clock className={`h-4 w-4 ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}>{phase.duration}</span>
                    </div>
                  </div>
                  <p className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                    {phase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
                isDarkMode ? 'text-slate-100' : 'text-slate-900'
              }`}>
                Why Choose Our Penetration Testing?
              </h2>
              <div className="space-y-6">
                {[
                  {
                    icon: Award,
                    title: "Certified Experts",
                    description: "Our team holds industry certifications including OSCP, CEH, and CISSP"
                  },
                  {
                    icon: FileText,
                    title: "Detailed Reporting",
                    description: "Comprehensive reports with executive summaries and technical details"
                  },
                  {
                    icon: Users,
                    title: "Remediation Support",
                    description: "Ongoing support to help you fix identified vulnerabilities"
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <benefit.icon className={`h-6 w-6 mt-1 flex-shrink-0 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div>
                      <h3 className={`font-semibold mb-1 ${
                        isDarkMode ? 'text-slate-100' : 'text-slate-900'
                      }`}>{benefit.title}</h3>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5380664/pexels-photo-5380664.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Security Testing"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
            </div>
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default PenetrationTestingPage;